import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Panel, Separator } from 'react-resizable-panels'
import { DisplayWindow, DisplayHeader, Display } from '../display'
import PdfViewer from '../pdf/PdfViewer'
import { standardPdfUrl } from '../../services/pdfs'
import { standardId } from '../../utils/standardRef'
import { locatePath } from './codeTree'
import { historyHopLabel } from './locationHistory'
import { CommentsPane } from './comments/CommentsPane'
import { GuidesPane } from './guides/GuidesPane'
import { PageMarks } from './PageMarks'
import './CodeDisplay.css'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

// Double chevron for the pane collapse/expand toggles; `dir` points toward the
// pane that grows (right = grow the left/viewer pane, left = grow comments).
// Crosshair / locate icon for "jump to the current section in the nav".
const IconLocate = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="7" />
    <line x1="12" y1="1" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="23" />
    <line x1="1" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="23" y2="12" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </svg>
)

const IconArrow = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'left' ? (
      <polyline points="15 6 9 12 15 18" />
    ) : (
      <polyline points="9 6 15 12 9 18" />
    )}
  </svg>
)

const IconChevrons = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'right' ? (
      <>
        <polyline points="7 6 13 12 7 18" />
        <polyline points="13 6 19 12 13 18" />
      </>
    ) : (
      <>
        <polyline points="17 6 11 12 17 18" />
        <polyline points="11 6 5 12 11 18" />
      </>
    )}
  </svg>
)

// The inner viewer | comments split for one code item: the standard's PDF on the
// left, an item-scoped comments thread on the right, with per-pane collapse
// toggles (mirroring the searchroom chat|PDF split). Reusable window content -
// the item title and window controls live in the surrounding window header.
// Split sizes persist via `onPanesChange` (stored on the grid window).
//
// Comments are item-scoped (standard + order), with descendant roll-up.
//
// How long a section must stay on screen before a scroll is remembered.
const HISTORY_DWELL = 500
// Ignore sections the PDF passes through while a jump is in flight.
const HISTORY_JUMP = 1600

// `items` is the standard's full item tree, used to resolve the live location:
// the viewer reports its most-visible page, which maps to the current section
// and renders as a breadcrumb in the viewer pane header (updates as you scroll).
// `history` / `onVisit` / `onHistory` are that window's section back/forward list.
function CodePanes({
  item,
  items,
  windowId,
  panes,
  onPanesChange,
  onRevealInNav,
  history,
  historyIndex,
  recall,
  onVisit,
  onHistory,
}) {
  const defaultLayout = useMemo(
    () => ({ viewer: panes?.viewer ?? 55, comments: panes?.comments ?? 45 }),
    [panes]
  )

  const viewerPanelRef = useRef(null)
  const commentsPanelRef = useRef(null)
  // 'none' | 'viewer' | 'comments' — which pane is collapsed. Derived from
  // onResize so it reflects manual drags and the persisted layout too.
  const [collapsed, setCollapsed] = useState('none')
  // Which notes surface the right pane shows: comments or guides.
  const [notesView, setNotesView] = useState('comments')
  // A page tag asked the notes pane to reveal one comment or guide.
  const [locate, setLocate] = useState(null)

  // `targetPage` drives the PDF (jumps on change); `currentPage` is what the
  // viewer reports as most-visible (drives the live breadcrumb). Opening a new
  // item resets the target to the item's start page.
  const [targetPage, setTargetPage] = useState(item?.pageStart)
  const [pageToken, setPageToken] = useState(0)
  const [currentPage, setCurrentPage] = useState(item?.pageStart || 1)
  // Ask the viewer to show `page`, even when that number is already the target
  // (a scroll leaves targetPage behind, so history back must still re-anchor).
  const requestPage = useCallback((page) => {
    if (page == null) return
    setTargetPage(page)
    setPageToken((n) => n + 1)
  }, [])
  useEffect(() => {
    setCurrentPage(item?.pageStart || 1)
    requestPage(item?.pageStart)
  }, [item?._id, item?.pageStart, requestPage])

  // The location trail (root → current section) for the visible page. The root
  // (standard) is dropped — the window header already shows the standard name.
  const location = useMemo(
    () => locatePath(items, currentPage).filter((it) => it.level > 0),
    [items, currentPage]
  )

  const onVisitRef = useRef(onVisit)
  onVisitRef.current = onVisit
  // While set, the PDF is moving to a chosen section. That landing is already
  // on the list (or is the item just opened); resting there must not push again
  // and drop the forward stack. `settled` is the section id last accounted for.
  const jumpRef = useRef(null)
  const settledRef = useRef(null)

  const leaf = location[location.length - 1] || null

  // Opening an item (sidebar click, or back/forward into another standard)
  // scrolls the PDF. Hold history until that scroll settles.
  useEffect(() => {
    if (!item?._id) return
    jumpRef.current = {
      itemId: item._id,
      pageStart: item.pageStart,
      until: Date.now() + HISTORY_JUMP,
    }
  }, [item?._id, item?.pageStart])

  // Back/forward within the same document: the open item stays, the page moves.
  // Skip the nonce this pane mounted with so a restored window doesn't re-jump.
  const recallNonce = recall?.nonce || 0
  const seenRecall = useRef(null)
  useEffect(() => {
    if (seenRecall.current == null) {
      seenRecall.current = recallNonce
      return
    }
    if (!recall || recallNonce === seenRecall.current) return
    seenRecall.current = recallNonce
    jumpRef.current = {
      itemId: recall.itemId,
      pageStart: recall.pageStart,
      until: Date.now() + HISTORY_JUMP,
    }
    if (recall.pageStart != null) requestPage(recall.pageStart)
  }, [recall, recallNonce, requestPage])

  // Remember a section once it has stayed on screen. A jump's landing section
  // is not recorded again; the next section the reader actually rests on is.
  useEffect(() => {
    if (!leaf?._id) return undefined
    let timer
    const tick = () => {
      const jump = jumpRef.current
      if (jump) {
        const arrived = jump.pageStart == null || currentPage === jump.pageStart
        if (!arrived && Date.now() < jump.until) {
          timer = setTimeout(tick, Math.max(30, jump.until - Date.now() + 30))
          return
        }
        jumpRef.current = null
        settledRef.current = leaf._id
        return
      }
      if (settledRef.current && String(settledRef.current) === String(leaf._id)) return
      settledRef.current = leaf._id
      onVisitRef.current?.(leaf)
    }
    timer = setTimeout(tick, HISTORY_DWELL)
    return () => clearTimeout(timer)
  }, [leaf, currentPage])

  // Jump to a section inside this document and remember it immediately.
  const jumpToSection = useCallback((section) => {
    if (!section || section.pageStart == null) return
    const id = section._id || section.item
    requestPage(section.pageStart)
    if (!id) return
    jumpRef.current = { itemId: id, pageStart: section.pageStart, until: Date.now() + HISTORY_JUMP }
    settledRef.current = id
    onVisitRef.current?.(section._id ? section : { ...section, _id: id })
  }, [requestPage])

  // Clicking an item chip in a comment jumps the viewer to that item's page.
  // The window's PDF is bound to this item's standard, so cross-standard tags
  // (a comment can span standards) can't open here — no-op in that case.
  const openTaggedItem = useCallback(
    (anchor) => {
      if (!anchor) return
      if (standardId(anchor.standard) && standardId(anchor.standard) !== standardId(item?.standard)) return
      jumpToSection(anchor)
    },
    [item?.standard, jumpToSection]
  )

  const backEntry = history?.[historyIndex - 1]
  const forwardEntry = history?.[historyIndex + 1]
  const backTitle = backEntry ? `Back to ${historyHopLabel(backEntry, item?.standard)}` : 'Back'
  const forwardTitle = forwardEntry
    ? `Forward to ${historyHopLabel(forwardEntry, item?.standard)}`
    : 'Forward'

  const onViewerResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'viewer' : c === 'viewer' ? 'none' : c))
  }, [])
  const onCommentsResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'comments' : c === 'comments' ? 'none' : c))
  }, [])

  // Each header toggle maximizes its own pane by collapsing the other.
  const toggleViewerMaximize = () => {
    if (collapsed === 'comments') commentsPanelRef.current?.expand()
    else commentsPanelRef.current?.collapse()
  }
  const toggleCommentsMaximize = () => {
    if (collapsed === 'viewer') viewerPanelRef.current?.expand()
    else viewerPanelRef.current?.collapse()
  }

  const revealInNotes = useCallback((kind, id) => {
    setNotesView(kind === 'guide' ? 'guides' : 'comments')
    if (collapsed === 'comments') commentsPanelRef.current?.expand()
    setLocate({ kind, id, n: Date.now() })
  }, [collapsed])

  const fileUrl = standardPdfUrl(item?.standard)
  const standardKey = standardId(item?.standard)

  return (
    <div className="code-display">
      <Group
        orientation="horizontal"
        className="code-panelgroup"
        defaultLayout={defaultLayout}
        onLayoutChanged={onPanesChange}
      >
        <Panel
          id="viewer"
          className="code-pane"
          defaultSize="55%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={viewerPanelRef}
          onResize={onViewerResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                actions={
                  <>
                    <button
                      type="button"
                      title={backTitle}
                      aria-label={backTitle}
                      disabled={!backEntry}
                      onClick={() => onHistory?.(-1)}
                    >
                      <IconArrow dir="left" />
                    </button>
                    <button
                      type="button"
                      title={forwardTitle}
                      aria-label={forwardTitle}
                      disabled={!forwardEntry}
                      onClick={() => onHistory?.(1)}
                    >
                      <IconArrow dir="right" />
                    </button>
                    <button
                      type="button"
                      title="Find current section in the sidebar"
                      aria-label="Find current section in the sidebar"
                      disabled={!location.length}
                      onClick={() => onRevealInNav?.(location)}
                    >
                      <IconLocate />
                    </button>
                    <button
                      type="button"
                      title={collapsed === 'comments' ? 'Restore split view' : 'Expand viewer pane'}
                      aria-label={
                        collapsed === 'comments' ? 'Restore split view' : 'Expand viewer pane'
                      }
                      className={collapsed === 'comments' ? 'is-active' : ''}
                      onClick={toggleViewerMaximize}
                    >
                      <IconChevrons dir={collapsed === 'comments' ? 'left' : 'right'} />
                    </button>
                  </>
                }
              >
                {location.length ? (
                  <nav className="code-viewer-loc" aria-label="Location">
                    {location.map((seg, i) => (
                      <span className="code-viewer-loc-seg" key={seg._id}>
                        {i > 0 && <span className="code-viewer-loc-sep">/</span>}
                        <button
                          type="button"
                          className="code-viewer-loc-link"
                          title={`Go to ${seg.label}`}
                          onClick={() => jumpToSection(seg)}
                        >
                          {seg.label}
                        </button>
                      </span>
                    ))}
                  </nav>
                ) : (
                  <span className="code-viewer-title" title={item?.label}>
                    {item?.label || 'Viewer'}
                  </span>
                )}
              </DisplayHeader>
            }
          >
            <Display fill>
              <div className="code-viewer-stage">
                {fileUrl ? (
                  <PdfViewer
                    key={standardKey}
                    fileUrl={fileUrl}
                    page={targetPage}
                    pageToken={pageToken}
                    standard={standardKey}
                    onPageChange={setCurrentPage}
                  />
                ) : (
                  <div className="display-empty">No document available for this item.</div>
                )}
                {fileUrl ? (
                  <PageMarks
                    standard={standardKey}
                    page={currentPage}
                    onOpenComment={(id) => revealInNotes('comment', id)}
                    onOpenGuide={(id) => revealInNotes('guide', id)}
                  />
                ) : null}
              </div>
            </Display>
          </DisplayWindow>
        </Panel>

        <Separator className="code-resize-handle" />

        <Panel
          id="comments"
          className="code-pane"
          defaultSize="45%"
          minSize="20%"
          collapsible
          collapsedSize="0%"
          panelRef={commentsPanelRef}
          onResize={onCommentsResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                title={
                  <select
                    className="code-notes-toggle"
                    value={notesView}
                    onChange={(e) => setNotesView(e.target.value)}
                    aria-label="Show comments or guides"
                  >
                    <option value="comments">Comments</option>
                    <option value="guides">Guides</option>
                  </select>
                }
                actions={
                  <button
                    type="button"
                    title={collapsed === 'viewer' ? 'Restore split view' : 'Expand notes pane'}
                    aria-label={
                      collapsed === 'viewer' ? 'Restore split view' : 'Expand notes pane'
                    }
                    className={collapsed === 'viewer' ? 'is-active' : ''}
                    onClick={toggleCommentsMaximize}
                  >
                    <IconChevrons dir={collapsed === 'viewer' ? 'right' : 'left'} />
                  </button>
                }
              />
            }
          >
            <Display fill>
              {notesView === 'comments' ? (
                <CommentsPane
                  item={item}
                  items={items}
                  windowId={windowId}
                  onOpenItem={openTaggedItem}
                  locate={locate?.kind === 'comment' ? locate : null}
                  onLocated={() => setLocate(null)}
                />
              ) : (
                <GuidesPane
                  item={item}
                  items={items}
                  windowId={windowId}
                  onOpenItem={openTaggedItem}
                  locate={locate?.kind === 'guide' ? locate : null}
                  onLocated={() => setLocate(null)}
                />
              )}
            </Display>
          </DisplayWindow>
        </Panel>
      </Group>
    </div>
  )
}

export default CodePanes
